import React, { useState } from "react";
import PropTypes from "prop-types";
import { AppUtils } from "@src/utils";

/** Image that reloads on error (with max attempts) to avoid broken images in the app
 *
 * @param {React.JSX.IntrinsicElements['img']} props
 */
export const ImageWithRetry = (props) => {
  const [retryAttempts, setRetryAttempts] = useState(0);
  const RETRY_MAX_ATTEMPTS = 2;
  const RETRY_ATTEMPT_TO_START_CACHE_BUSTING = 1;
  const RETRY_DELAY = 200;

  const handleError = React.useCallback(
    (e) => {
      console.warn("Error loading image", e);

      if (retryAttempts < RETRY_MAX_ATTEMPTS) {
        setTimeout(() => setRetryAttempts((prev) => prev + 1), RETRY_DELAY);
      } else {
        console.error(
          `Failed loading image after ${retryAttempts + 1} attempts.`,
        );
        props?.onError?.(e);
      }
    },
    [retryAttempts, props],
  );

  //Retry Method 1: Cache busting URL
  const imgSrc = React.useMemo(() => {
    let imgSrc = props.src;
    if (retryAttempts >= RETRY_ATTEMPT_TO_START_CACHE_BUSTING) {
      //Add cache-busting query string
      imgSrc = AppUtils.setUrlQueryParams(imgSrc, {
        _: `${retryAttempts}${Date.now()}`,
      });
    }
    return imgSrc;
  }, [props.src, retryAttempts]);

  return (
    <img
      //swapping the key should make react remount the element
      key={retryAttempts}
      {...props}
      src={imgSrc}
      onError={handleError}
    />
  );
};

export const imageWithRetryControls = {
  src: {
    propType: PropTypes.string.isRequired,
    description: "Image source URL",
    controlType: "text",
  },
  className: {
    propType: PropTypes.string,
    description: "Alternative text for the image",
    controlType: "text",
    defaultValue: "",
  },
  //...(Any other img attributes)
};
export default ImageWithRetry;
